import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Plus, Search, MapPin, Users, Wrench, ChevronRight, UserPlus, UserMinus, Edit, Trash2 } from 'lucide-react';
import api from '../lib/api';
import { fmtDate } from '../lib/dateUtils';
import { useAuth } from '../contexts/AuthContext';

function SiteCard({ site, onClick }) {
  const statusMap = {
    pianificato: 'bg-slate-100 text-slate-600 border-slate-200',
    attivo: 'bg-green-50 text-green-700 border-green-200',
    sospeso: 'bg-amber-50 text-amber-700 border-amber-200',
    chiuso: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={onClick} data-testid={`site-card-${site.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold text-slate-900">{site.name}</h3>
            <p className="text-xs text-slate-500">{site.client}</p>
          </div>
          <Badge variant="outline" className={statusMap[site.status] || statusMap.pianificato}>{site.status}</Badge>
        </div>
        <div className="flex items-center gap-1 text-xs text-slate-500 mb-3">
          <MapPin size={12} />{site.location || 'N/A'}
        </div>
        <div className="flex items-center gap-4 text-xs text-slate-600">
          <span className="flex items-center gap-1"><Users size={12} />{site.employee_count || 0} operatori</span>
          <span className="flex items-center gap-1"><Wrench size={12} />{site.equipment_count || 0} attrezzature</span>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SitesPage() {
  const { isRole } = useAuth();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [showAssignEmp, setShowAssignEmp] = useState(false);
  const [showAssignEquip, setShowAssignEquip] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [assignId, setAssignId] = useState('');
  const [form, setForm] = useState({ name: '', client: '', location: '', start_date: '', expected_end_date: '', status: 'pianificato', description: '', notes: '' });
  const [editForm, setEditForm] = useState({ name: '', client: '', location: '', start_date: '', expected_end_date: '', status: 'pianificato', description: '', notes: '' });

  const load = async () => {
    try {
      const [sitesRes, empRes, eqRes] = await Promise.all([
        api.get('/sites', { params: { search: search || undefined } }),
        api.get('/employees').catch(() => ({ data: [] })),
        api.get('/equipment').catch(() => ({ data: [] })),
      ]);
      setSites(sitesRes.data);
      setEmployees(empRes.data);
      setEquipment(eqRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [search]);

  const handleCreate = async () => {
    await api.post('/sites', form);
    setShowCreate(false);
    setForm({ name: '', client: '', location: '', start_date: '', expected_end_date: '', status: 'pianificato', description: '', notes: '' });
    load();
  };

  const openDetail = async (site) => {
    try {
      const res = await api.get(`/sites/${site.id}`);
      setSelectedSite(res.data);
      setShowDetail(true);
    } catch (err) { console.error(err); }
  };

  const assignEmployee = async () => {
    if (!assignId || !selectedSite) return;
    await api.post(`/sites/${selectedSite.id}/assign-employee`, { employee_id: assignId });
    setShowAssignEmp(false);
    setAssignId('');
    const res = await api.get(`/sites/${selectedSite.id}`);
    setSelectedSite(res.data);
    load();
  };

  const unassignEmployee = async (empId) => {
    await api.post(`/sites/${selectedSite.id}/unassign-employee`, { employee_id: empId });
    const res = await api.get(`/sites/${selectedSite.id}`);
    setSelectedSite(res.data);
    load();
  };

  const assignEquipment = async () => {
    if (!assignId || !selectedSite) return;
    await api.post(`/sites/${selectedSite.id}/assign-equipment`, { equipment_id: assignId });
    setShowAssignEquip(false);
    setAssignId('');
    const res = await api.get(`/sites/${selectedSite.id}`);
    setSelectedSite(res.data);
    load();
  };

  const unassignEquipment = async (eqId) => {
    await api.post(`/sites/${selectedSite.id}/unassign-equipment`, { equipment_id: eqId });
    const res = await api.get(`/sites/${selectedSite.id}`);
    setSelectedSite(res.data);
    load();
  };

  const openEdit = () => {
    if (selectedSite) {
      setEditForm({
        name: selectedSite.name || '',
        client: selectedSite.client || '',
        location: selectedSite.location || '',
        start_date: selectedSite.start_date || '',
        expected_end_date: selectedSite.expected_end_date || '',
        status: selectedSite.status || 'pianificato',
        description: selectedSite.description || '',
        notes: selectedSite.notes || '',
      });
      setShowEdit(true);
    }
  };

  const handleEdit = async () => {
    try {
      await api.put(`/sites/${selectedSite.id}`, editForm);
      setShowEdit(false);
      const res = await api.get(`/sites/${selectedSite.id}`);
      setSelectedSite(res.data);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore durante la modifica');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Sei sicuro di voler eliminare il cantiere "${selectedSite?.name}"? Questa operazione è irreversibile.`)) return;
    try {
      await api.delete(`/sites/${selectedSite.id}`);
      setShowDetail(false);
      setSelectedSite(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore durante l\'eliminazione');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sites-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Cantieri</h1>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 sm:w-64">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Cerca cantiere..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="site-search" />
          </div>
          {isRole('admin', 'aspp', 'qhse', 'capo_cantiere') && (
            <Button onClick={() => setShowCreate(true)} data-testid="add-site-btn"><Plus size={16} className="mr-1" /> Nuovo</Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-32 bg-slate-200 rounded-lg animate-pulse" />)}
        </div>
      ) : sites.length === 0 ? (
        <Card className="border-slate-200"><CardContent className="p-8 text-center text-slate-500">Nessun cantiere trovato</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sites.map(site => <SiteCard key={site.id} site={site} onClick={() => openDetail(site)} />)}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuovo Cantiere</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5"><Label>Nome *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="site-name" /></div>
            <div className="space-y-1.5"><Label>Cliente</Label><Input value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Localita</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Data Inizio</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Data Fine Prevista</Label><Input type="date" value={form.expected_end_date} onChange={(e) => setForm({ ...form, expected_end_date: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Stato</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pianificato">Pianificato</SelectItem>
                  <SelectItem value="attivo">Attivo</SelectItem>
                  <SelectItem value="sospeso">Sospeso</SelectItem>
                  <SelectItem value="chiuso">Chiuso</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5"><Label>Descrizione</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annulla</Button>
            <Button onClick={handleCreate} disabled={!form.name} data-testid="site-save-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedSite && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle className="text-xl">{selectedSite.name}</DialogTitle>
                    <p className="text-sm text-slate-500">{selectedSite.client} - {selectedSite.location}</p>
                  </div>
                  {isRole('admin', 'aspp', 'qhse', 'capo_cantiere') && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={openEdit} data-testid="edit-site-btn">
                        <Edit size={14} className="mr-1" /> Modifica
                      </Button>
                      <Button size="sm" variant="destructive" onClick={handleDelete} data-testid="delete-site-btn">
                        <Trash2 size={14} className="mr-1" /> Elimina
                      </Button>
                    </div>
                  )}
                </div>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-slate-500">Stato:</span> <Badge variant="outline" className="ml-1">{selectedSite.status}</Badge></div>
                  <div><span className="text-slate-500">Inizio:</span> <span className="font-medium ml-1">{selectedSite.start_date ? fmtDate(selectedSite.start_date) : '-'}</span></div>
                </div>

                {/* Assigned Employees */}
                <Card className="border-slate-200">
                  <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-sm">Personale Assegnato ({selectedSite.employees_detail?.length || 0})</CardTitle>
                    {isRole('admin', 'aspp', 'qhse', 'capo_cantiere') && <Button size="sm" variant="outline" onClick={() => { setShowAssignEmp(true); setAssignId(''); }} data-testid="assign-emp-btn"><UserPlus size={14} className="mr-1" />Assegna</Button>}
                  </CardHeader>
                  <CardContent className="pt-0">
                    {selectedSite.employees_detail?.length === 0 ? (
                      <p className="text-sm text-slate-500 py-2">Nessun dipendente assegnato</p>
                    ) : (
                      <div className="space-y-2">
                        {selectedSite.employees_detail?.map(emp => (
                          <div key={emp.id} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                            <div>
                              <p className="text-sm font-medium">{emp.first_name} {emp.last_name}</p>
                              <p className="text-xs text-slate-500">{emp.role_title}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className={emp.eligibility_status === 'idoneo' ? 'bg-green-50 text-green-700 border-green-200' : emp.eligibility_status === 'non_idoneo' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200'}>{emp.eligibility_status}</Badge>
                              {isRole('admin', 'aspp', 'qhse', 'capo_cantiere') && <Button size="sm" variant="ghost" onClick={() => unassignEmployee(emp.id)} className="text-red-500 hover:text-red-700"><UserMinus size={14} /></Button>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Assigned Equipment */}
                <Card className="border-slate-200">
                  <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-sm">Attrezzature Assegnate ({selectedSite.equipment_detail?.length || 0})</CardTitle>
                    {isRole('admin', 'aspp', 'qhse', 'capo_cantiere') && <Button size="sm" variant="outline" onClick={() => { setShowAssignEquip(true); setAssignId(''); }} data-testid="assign-equip-btn"><Plus size={14} className="mr-1" />Assegna</Button>}
                  </CardHeader>
                  <CardContent className="pt-0">
                    {selectedSite.equipment_detail?.length === 0 ? (
                      <p className="text-sm text-slate-500 py-2">Nessuna attrezzatura assegnata</p>
                    ) : (
                      <div className="space-y-2">
                        {selectedSite.equipment_detail?.map(eq => (
                          <div key={eq.id} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                            <p className="text-sm font-medium">{eq.name} <span className="text-xs text-slate-500">({eq.internal_code})</span></p>
                            {isRole('admin', 'aspp', 'qhse', 'capo_cantiere') && <Button size="sm" variant="ghost" onClick={() => unassignEquipment(eq.id)} className="text-red-500 hover:text-red-700"><UserMinus size={14} /></Button>}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Employee */}
      <Dialog open={showAssignEmp} onOpenChange={setShowAssignEmp}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assegna Dipendente</DialogTitle></DialogHeader>
          <Select value={assignId} onValueChange={setAssignId}>
            <SelectTrigger data-testid="assign-emp-select"><SelectValue placeholder="Seleziona dipendente..." /></SelectTrigger>
            <SelectContent>{employees.filter(e => e.is_active && !selectedSite?.assigned_employees?.includes(e.id)).map(e => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name} - {e.role_title}</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter><Button onClick={assignEmployee} disabled={!assignId} data-testid="confirm-assign-emp">Assegna</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Equipment */}
      <Dialog open={showAssignEquip} onOpenChange={setShowAssignEquip}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assegna Attrezzatura</DialogTitle></DialogHeader>
          <Select value={assignId} onValueChange={setAssignId}>
            <SelectTrigger data-testid="assign-equip-select"><SelectValue placeholder="Seleziona attrezzatura..." /></SelectTrigger>
            <SelectContent>{equipment.filter(eq => eq.status === 'disponibile').map(eq => <SelectItem key={eq.id} value={eq.id}>{eq.name} ({eq.internal_code})</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter><Button onClick={assignEquipment} disabled={!assignId} data-testid="confirm-assign-equip">Assegna</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Site Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifica Cantiere</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5"><Label>Nome *</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-site-name" /></div>
            <div className="space-y-1.5"><Label>Cliente</Label><Input value={editForm.client} onChange={(e) => setEditForm({ ...editForm, client: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Localita</Label><Input value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Data Inizio</Label><Input type="date" value={editForm.start_date} onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Data Fine Prevista</Label><Input type="date" value={editForm.expected_end_date} onChange={(e) => setEditForm({ ...editForm, expected_end_date: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Stato</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pianificato">Pianificato</SelectItem>
                  <SelectItem value="attivo">Attivo</SelectItem>
                  <SelectItem value="sospeso">Sospeso</SelectItem>
                  <SelectItem value="chiuso">Chiuso</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5"><Label>Descrizione</Label><Textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Note</Label><Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)}>Annulla</Button>
            <Button onClick={handleEdit} disabled={!editForm.name} data-testid="edit-site-save-btn">Salva Modifiche</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
